import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import './OpeningStockModal.css';

const OpeningStockModal = ({ product, locations, onClose, onSave }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [apiData, setApiData] = useState(null);
  
  const [openingStockRows, setOpeningStockRows] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');

  // Fetch opening stock data on mount
  useEffect(() => {
    if (product?.id) {
      fetchOpeningStock();
    }
  }, [product?.id]);

  const fetchOpeningStock = async () => {
    try {
      setLoading(true);
      const res = await apiService.getOpeningStock(product.id);
      if (res.success && res.data) {
        setApiData(res.data);
        
        // Use locations from API or fallback to props
        const fetchedLocations = res.data.locations || locations || [];
        const defaultLoc = fetchedLocations[0]?.id || '';
        setSelectedLocation(defaultLoc);
        
        initializeRows(res.data, defaultLoc);
      } else {
        alert(res.message || 'Failed to load opening stock data');
      }
    } catch (err) {
      /* console.error('Error fetching opening stock:', err); */
      alert('Error loading opening stock: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const initializeRows = (data, locationId) => {
    const productDetail = data.product;
    const existingStocks = data.existing_stocks || {};
    // existing_stocks is nested: existing_stocks[location_id][variation_id]
    const locStocks = existingStocks[locationId] || {};
    
    let rows = [];
    
    if (productDetail && productDetail.variations && productDetail.variations.length > 0) {
      productDetail.variations.forEach(variation => {
        const varStocks = locStocks[variation.id] || [];
        if (varStocks.length > 0) {
          varStocks.forEach(stock => {
            rows.push({
              id: stock.purchase_line_id || (Date.now() + Math.random()),
              purchase_line_id: stock.purchase_line_id,
              variation_id: variation.id,
              variation_name: variation.name === 'DUMMY' ? '' : variation.name,
              qty: stock.quantity || 0,
              cost: stock.purchase_price || 0,
              date: stock.transaction_date ? stock.transaction_date.split(' ')[0] : new Date().toISOString().split('T')[0],
              note: stock.purchase_line_note || '',
              lot_number: stock.lot_number || '',
              exp_date: stock.exp_date || ''
            });
          });
        } else {
          rows.push({
            id: Date.now() + Math.random(),
            purchase_line_id: null,
            variation_id: variation.id,
            variation_name: variation.name === 'DUMMY' ? '' : variation.name,
            qty: '',
            cost: '',
            date: new Date().toISOString().split('T')[0],
            note: '',
            lot_number: '',
            exp_date: ''
          });
        }
      });
    } else {
      // Fallback if variations list is empty
      rows.push({
        id: Date.now(),
        purchase_line_id: null,
        variation_id: null,
        variation_name: '',
        qty: '',
        cost: '',
        date: new Date().toISOString().split('T')[0],
        note: '',
        lot_number: '',
        exp_date: ''
      });
    }
    
    setOpeningStockRows(rows);
  };

  const handleLocationChange = (locId) => {
    setSelectedLocation(locId);
    if (apiData) {
      initializeRows(apiData, locId);
    }
  };

  const handleAddRow = (variationId, variationName) => {
    setOpeningStockRows(prev => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        purchase_line_id: null,
        variation_id: variationId,
        variation_name: variationName,
        qty: '',
        cost: '',
        date: new Date().toISOString().split('T')[0],
        note: '',
        lot_number: '',
        exp_date: ''
      }
    ]);
  };

  const handleRemoveRow = (id, variationId) => {
    const variationRows = openingStockRows.filter(row => row.variation_id === variationId);
    if (variationRows.length > 1) {
      setOpeningStockRows(prev => prev.filter(row => row.id !== id));
    } else {
      // Clear values if it's the last remaining row for this variation
      setOpeningStockRows(prev => prev.map(row => 
        row.id === id ? {
          ...row,
          qty: '',
          cost: '',
          date: new Date().toISOString().split('T')[0],
          note: '',
          lot_number: '',
          exp_date: '',
          purchase_line_id: null
        } : row
      ));
    }
  };

  const handleChange = (id, field, value) => {
    setOpeningStockRows(prev => prev.map(row => 
      row.id === id ? { ...row, [field]: value } : row
    ));
  };

  const handleSave = async () => {
    if (!selectedLocation) {
      alert('Please select a business location');
      return;
    }

    try {
      setSaving(true);
      
      const stocks = {};
      stocks[selectedLocation] = {};
      
      openingStockRows.forEach(row => {
        const qty = parseFloat(row.qty);
        const cost = parseFloat(row.cost);
        
        // We send rows that have a purchase_line_id (to update/delete)
        // OR rows that have a positive quantity (to add/update)
        if (row.purchase_line_id || (!isNaN(qty) && qty > 0)) {
          if (!stocks[selectedLocation][row.variation_id]) {
            stocks[selectedLocation][row.variation_id] = [];
          }
          
          stocks[selectedLocation][row.variation_id].push({
            quantity: isNaN(qty) ? 0 : qty,
            purchase_price: isNaN(cost) ? 0 : cost,
            purchase_line_id: row.purchase_line_id || undefined,
            exp_date: row.exp_date || undefined,
            lot_number: row.lot_number || undefined,
            transaction_date: row.date || undefined,
            purchase_line_note: row.note || undefined
          });
        }
      });
      
      const payload = {
        stocks: stocks
      };
      
      const res = await apiService.saveOpeningStock(product.id, payload);
      if (res.success) {
        if (onSave) onSave(res.data);
        onClose();
      } else {
        alert(res.message || 'Failed to save opening stock');
      }
    } catch (err) {
      /* console.error('Error saving opening stock:', err); */
      alert('Error saving opening stock: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const totalAmount = openingStockRows.reduce((sum, row) => {
    const qty = parseFloat(row.qty) || 0;
    const cost = parseFloat(row.cost) || 0;
    return sum + (qty * cost);
  }, 0);

  if (!product) return null;

  const activeLocations = apiData?.locations || locations || [];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="modal-container opening-stock-modal" 
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: '1100px', width: '95%' }}
      >
        <div className="modal-header">
          <h2>Add or edit Opening Stock</h2>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>
        
        <div className="modal-content" style={{ width: '100%', maxWidth: 'none' }}>
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <p>Loading opening stock details...</p>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '2rem', alignItems: 'center' }}>
                <div style={{ fontSize: '0.95rem', fontWeight: '500' }}>
                  Product: <span style={{ color: 'var(--etpos-primary, #3b82f6)' }}>{product.name}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <label style={{ fontWeight: '600', fontSize: '0.9rem' }}>Business Location:</label>
                  <select 
                    value={selectedLocation} 
                    onChange={(e) => handleLocationChange(e.target.value)}
                    style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                  >
                    {activeLocations.map(loc => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <table className="opening-stock-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ minWidth: '150px' }}>Product Name</th>
                    <th style={{ minWidth: '160px' }}>Quantity Remaining</th>
                    <th style={{ minWidth: '150px' }}>Unit Cost (Before Tax)</th>
                    <th style={{ minWidth: '130px', textAlign: 'center' }}>Subtotal (Before Tax)</th>
                    <th style={{ minWidth: '150px' }}>Date</th>
                    <th style={{ minWidth: '200px' }}>Note</th>
                    <th style={{ width: '50px', textAlign: 'center' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {openingStockRows.map((row, idx) => {
                    const subtotal = (parseFloat(row.qty) || 0) * (parseFloat(row.cost) || 0);
                    return (
                      <tr key={row.id}>
                        <td>
                          {product.name}
                          {row.variation_name ? ` (${row.variation_name})` : ''}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <input
                              type="number"
                              value={row.qty}
                              placeholder="0.00"
                              onChange={(e) => handleChange(row.id, 'qty', e.target.value)}
                            />
                            <span style={{ display: 'flex', alignItems: 'center', color: '#64748b' }}>
                              {product.unit || 'Unit'}
                            </span>
                          </div>
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            value={row.cost}
                            placeholder="0.00"
                            onChange={(e) => handleChange(row.id, 'cost', e.target.value)}
                          />
                        </td>
                        <td style={{ textAlign: 'center', fontWeight: '600' }}>
                          {subtotal.toFixed(2)}
                        </td>
                        <td>
                          <input
                            type="date"
                            value={row.date}
                            onChange={(e) => handleChange(row.id, 'date', e.target.value)}
                          />
                        </td>
                        <td>
                          <textarea
                            value={row.note}
                            rows="1"
                            onChange={(e) => handleChange(row.id, 'note', e.target.value)}
                            style={{ resize: 'vertical' }}
                          />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {/* If multiple rows for this variation, show delete, else show add */}
                          {openingStockRows.filter(r => r.variation_id === row.variation_id).indexOf(row) === 0 ? (
                            <button className="action-btn-add" onClick={() => handleAddRow(row.variation_id, row.variation_name)}>+</button>
                          ) : (
                            <button className="action-btn-remove" onClick={() => handleRemoveRow(row.id, row.variation_id)}>✕</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="opening-stock-total-row">
                    <td colSpan={3} style={{ textAlign: 'right' }}>Total Amount (Exc. Tax):</td>
                    <td style={{ textAlign: 'center' }}>{totalAmount.toFixed(2)}</td>
                    <td colSpan={3}></td>
                  </tr>
                </tbody>
              </table>
            </>
          )}
        </div>

        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
          <button className="modal-btn primary" onClick={handleSave} disabled={loading || saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button className="modal-btn secondary" onClick={onClose} disabled={saving}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default OpeningStockModal;
